/**
 * LazyImage
 *
 * 一个统一的图片组件，处理：
 * - 浏览器原生 lazy loading
 * - 加载中骨架占位（保留卡片高度）
 * - 加载失败回落到原图
 * - decoding="async" 提示浏览器异步解码
 *
 * 用法：
 *   <LazyImage work={work} size="small" alt="..." className="w-full h-full object-cover" />
 *   <LazyImage src="..." fallbackSrc="..." alt="..." />
 */

import { useEffect, useRef, useState } from 'react';
import type { ImgHTMLAttributes } from 'react';
import { getDisplayUrl, type ImageDisplaySize } from '../utils/imageDisplay';
import { withAuthToken } from '../utils/storageUrl';

type DisplayWork = {
  resultUrl?: string | null;
  thumbnailUrl?: string | null;
  thumbnailMediumUrl?: string | null;
  base64Data?: string | null;
};

type LazyImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'onError'> & {
  /** 提供 work 时按 size 自动取图，并在失败时回落到原图。 */
  work?: DisplayWork | null;
  /** 当 work 缺省时直接使用的 src。 */
  src?: string | null;
  /** 当 src 加载失败时使用的回落地址。 */
  fallbackSrc?: string | null;
  size?: ImageDisplaySize;
};

export function LazyImage({
  work,
  src,
  fallbackSrc,
  size = 'small',
  alt = '',
  loading = 'lazy',
  decoding = 'async',
  className,
  style,
  ...rest
}: LazyImageProps) {
  // 计算最终展示的 URL
  const initialSrc = withAuthToken(
    work !== undefined && work !== null ? getDisplayUrl(work, size) ?? '' : src ?? ''
  );

  const [currentSrc, setCurrentSrc] = useState(initialSrc);
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const triedFallbackRef = useRef(false);

  // 当外部 work / src 变化时同步
  useEffect(() => {
    setCurrentSrc(initialSrc);
    setLoaded(false);
    setErrored(false);
    triedFallbackRef.current = false;
  }, [initialSrc]);

  const handleError = () => {
    if (triedFallbackRef.current) {
      setErrored(true);
      return;
    }
    triedFallbackRef.current = true;

    // 推导回落地址：优先用显式 fallbackSrc，其次用 work.resultUrl
    const fallback =
      fallbackSrc ??
      (work ? work.resultUrl ?? work.base64Data ?? null : null);

    if (fallback && fallback !== currentSrc) {
      // 回落地址同样需要令牌（回落可能来自未包装的 resultUrl / fallbackSrc）
      setCurrentSrc(withAuthToken(fallback));
      return;
    }
    setErrored(true);
  };

  if (!currentSrc || errored) {
    // 占位（保持父容器高度），不暴露错误信息给用户
    return (
      <div
        className={`bg-light-hover/40 flex items-center justify-center ${className ?? ''}`}
        style={style}
        aria-label={errored ? '图片加载失败' : '图片占位'}
      >
        <span className="text-xs text-slate-400">{errored ? '加载失败' : ''}</span>
      </div>
    );
  }

  return (
    <>
      {!loaded && (
        <div
          className={`absolute inset-0 animate-pulse bg-light-hover/40 ${className ?? ''}`}
          aria-hidden
          style={style}
        />
      )}
      <img
        src={currentSrc}
        alt={alt}
        loading={loading}
        decoding={decoding}
        onLoad={() => setLoaded(true)}
        onError={handleError}
        className={className}
        style={style}
        {...rest}
      />
    </>
  );
}

export default LazyImage;
